import React, { useState, useEffect } from 'react';
import { Device } from '../../lib/types';
import { SUPPORT_EMAIL } from '../../lib/emails';
import Modal, { ModalTitle, ModalSubtitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import styles from './Devices.module.scss';

interface SendSmsModalProps {
  open: boolean;
  onClose: () => void;
  device: Device | null;
  orgName: string;
  onSendSms: (phoneNumber: string, message: string) => void;
}

function getRecipient(device: Device) {
  // Foster → use holder info; Pending/Completed adoption → use adopter info
  if (device.holderType === 'foster') {
    return {
      name: device.holderName,
      phone: device.holderPhone,
    };
  }
  return {
    name: device.adopterName,
    phone: device.adopterPhone,
  };
}

function buildDefaultMessage(recipientName: string | null, orgName: string): string {
  const greeting = recipientName ? `Hi ${recipientName}` : 'Hi';
  return `${greeting}, this is ${orgName}. Please complete your Fi Mini GPS setup — download the Fi app here: https://fitracking.com/app/join and follow the instructions in your setup email. Questions? Email ${SUPPORT_EMAIL}`;
}

export default function SendSmsModal({ open, onClose, device, orgName, onSendSms }: SendSmsModalProps) {
  const [message, setMessage] = useState('');

  // Reset message when device changes or modal opens
  useEffect(() => {
    if (open && device) {
      const recipient = getRecipient(device);
      setMessage(buildDefaultMessage(recipient.name, orgName));
    }
  }, [open, device, orgName]);

  if (!device) {
    return null;
  }

  const recipient = getRecipient(device);

  if (!recipient.phone) {
    return (
      <Modal open={open} onClose={onClose}>
        <ModalTitle>Send SMS</ModalTitle>
        <ModalSubtitle>No phone number on file for {recipient.name || 'this recipient'}.</ModalSubtitle>
        <ModalActions>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </ModalActions>
      </Modal>
    );
  }

  function handleSend() {
    onSendSms(recipient.phone!, message);
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose}>
      <ModalTitle>Send SMS</ModalTitle>
      <ModalSubtitle>
        To: {recipient.name || 'Unknown'} ({recipient.phone})
      </ModalSubtitle>
      <div className={styles.smsTextareaWrapper}>
        <textarea
          className={styles.smsTextarea}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={5}
        />
        <div className={styles.smsCharCount}>{message.length} characters</div>
      </div>
      <ModalActions>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSend} disabled={!message.trim()}>
          Send SMS
        </Button>
      </ModalActions>
    </Modal>
  );
}
